import { useState } from "react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { CalendarBlankIcon, CaretLeftIcon, CaretRightIcon, CaretDownIcon } from "@phosphor-icons/react";

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const FULL = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];

// Click-only month + year picker. value / onChange use "YYYY-MM" ("" = none). Months after `max` are disabled.
export function MonthYearPicker({ value, onChange, max, active, testId = "month-picker" }) {
  const [maxY, maxM] = (max || "9999-12").split("-").map(Number);
  const [open, setOpen] = useState(false);
  const [year, setYear] = useState(value ? Number(value.slice(0, 4)) : maxY);
  const label = value ? `${FULL[Number(value.slice(5, 7)) - 1]} ${value.slice(0, 4)}` : "Select month";

  const pick = (m) => { onChange(`${year}-${String(m + 1).padStart(2, "0")}`); setOpen(false); };

  return (
    <Popover open={open} onOpenChange={(o) => { setOpen(o); if (o) setYear(value ? Number(value.slice(0, 4)) : maxY); }}>
      <PopoverTrigger asChild>
        <button type="button" data-testid={`${testId}-trigger`}
          className={`h-10 sm:h-9 px-3 min-w-[160px] flex items-center justify-between gap-2 bg-[var(--bg-0)] border rounded-md text-xs font-mono uppercase tracking-widest transition-colors ${active ? "border-[var(--accent)] text-white" : "border-[var(--border)] text-zinc-400 hover:border-[var(--accent)]"}`}>
          <span className="flex items-center gap-2"><CalendarBlankIcon size={14} />{label}</span>
          <CaretDownIcon size={12} />
        </button>
      </PopoverTrigger>
      <PopoverContent align="center" className="w-64 p-3 bg-[var(--bg-1)] border-[var(--border)]" data-testid={`${testId}-panel`}>
        <div className="flex items-center justify-between mb-3">
          <button type="button" data-testid={`${testId}-prev-year`} onClick={() => setYear(y => y - 1)}
            className="h-7 w-7 flex items-center justify-center rounded-sm border border-[var(--border)] text-zinc-300 hover:bg-[var(--bg-2)]"><CaretLeftIcon size={12} /></button>
          <span className="font-mono text-sm text-white" data-testid={`${testId}-year`}>{year}</span>
          <button type="button" data-testid={`${testId}-next-year`} onClick={() => setYear(y => y + 1)} disabled={year >= maxY}
            className="h-7 w-7 flex items-center justify-center rounded-sm border border-[var(--border)] text-zinc-300 hover:bg-[var(--bg-2)] disabled:opacity-30 disabled:cursor-not-allowed"><CaretRightIcon size={12} /></button>
        </div>
        <div className="grid grid-cols-3 gap-2">
          {MONTHS.map((m, i) => {
            const key = `${year}-${String(i + 1).padStart(2, "0")}`;
            const disabled = year > maxY || (year === maxY && i + 1 > maxM);
            const selected = key === value;
            return (
              <button key={m} type="button" disabled={disabled} onClick={() => pick(i)} data-testid={`${testId}-month-${i + 1}`}
                className={`h-9 rounded-sm text-xs font-mono uppercase tracking-widest border transition-colors disabled:opacity-30 disabled:cursor-not-allowed ${selected ? "bg-[var(--accent)] border-[var(--accent)] text-white" : "border-[var(--border)] text-zinc-300 hover:bg-[var(--bg-2)]"}`}>
                {m}
              </button>
            );
          })}
        </div>
      </PopoverContent>
    </Popover>
  );
}
